import { useMemo } from 'react'
import { useFirestoreNoCompagny } from '../firebase/useFirestore'

// De teksten achter de ID's in een zichtbaarheidsregel (`ShowWhen`).
//
// Een regel bewaart antwoordopties, matrixrijen en -kolommen als uuid — met
// opzet, want een hernoemde optie mag de tak niet stilleggen. Wie de regel aan
// een mens wil tonen, moet die uuid's dus eerst terugvertalen. Dat recept stond
// in `QuestionData` en zou bij de printpagina een tweede keer ontstaan.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
// AI-CONTEXT: See docs/features/conditional-questions.md
//
// DE OPZOEKINGEN ZIJN GEGATE OP HET TYPE VAN DE TRIGGERVRAAG. Een schaal- of
// nummervraag heeft niets op te zoeken (die waarde is zelf leesbaar), een
// meerkeuzevraag alleen opties, een matrixvraag alleen rijen en kolommen.
// Ongegate kost dit vier listeners per conditionele vraag, op elke grafiek en
// elk printvel. De hook-engine slaat een query met '' helemaal over.

/**
 * @typedef {object} ShowWhenLabels
 * @property {object|undefined} triggerField - Het `QuestionnaireFields`-doc van
 *   de triggervraag; `undefined` als er geen regel is of de vraag verwijderd is
 * @property {Object<string, string>} labels - Optie-, rij- en kolom-ID's naar
 *   hun tekst; leeg als er niets op te zoeken viel
 * @property {boolean} loaded - Of de opzoekingen die deze regel nodig heeft
 *   beantwoord zijn. Zolang dit `false` is, is `triggerField` afwezig omdát de
 *   query nog loopt — niet omdat de vraag verwijderd is, en `labels` mist dan
 *   nog de tekst die de zin compleet maakt. Wie de regel aan een lezer toont
 *   moet hierop wachten, anders staat er even een onware bewering.
 *   AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 */

/**
 * @param {object} field - Het veld met (mogelijk) een `ShowWhen`
 * @param {object} [options]
 * @param {boolean} [options.enabled] - Op `false` wordt er niets opgehaald.
 *   Nodig voor de sjabloonweergave: die render is cross-tenant en komt leeg
 *   terug, wat de zin stil onvolledig zou maken.
 *   AI-CONTEXT: See docs/pitfalls.md#state-firestore-lookup-empty-in-template-mode
 * @param {(collection: string, field: string, id: string) => Array<object>|string} [options.queryHook]
 *   De opzoek-hook. Standaard cross-tenant (`useFirestoreNoCompagny`), want de
 *   analysekant toont ook vragenlijsten van andere omgevingen. Een scherm dat
 *   binnen één omgeving blijft geeft hier de tenant-gescopede variant mee.
 * @returns {ShowWhenLabels}
 */
const useShowWhenLabels = (field, options = {}) => {
    const { enabled = true, queryHook = useFirestoreNoCompagny } = options

    const triggerId = (enabled && field?.ShowWhen?.FieldID) || ''

    const triggerFields = queryHook('QuestionnaireFields', 'ID', triggerId)
    const triggerField = triggerFields?.[0]
    const triggerType = triggerField?.Type || ''

    const optionsId = (triggerType === 'multiple-one' || triggerType === 'multiple-multiple') ? triggerId : ''
    const matrixId = triggerType === 'matrix-one' ? triggerId : ''

    const triggerOptions = queryHook('MultipleQuestionOptions', 'Field', optionsId)
    const triggerRows = queryHook('MatrixQuestionRows', 'Field', matrixId)
    const triggerColumns = queryHook('MatrixQuestionColumns', 'Field', matrixId)

    // De engine geeft '' zolang een query loopt of overgeslagen wordt, en een
    // array zodra hij beantwoord is — ook als die array leeg is.
    const answered = (docs) => docs !== ''
    const loaded = !triggerId
        ? true
        : answered(triggerFields)
            && (!optionsId || answered(triggerOptions))
            && (!matrixId || (answered(triggerRows) && answered(triggerColumns)))

    const labels = useMemo(() => {
        /** @type {Object<string, string>} */
        const result = {}
        ;(triggerOptions || []).forEach((option) => { if (option?.ID) result[option.ID] = option.Option })
        ;(triggerRows || []).forEach((row) => { if (row?.ID) result[row.ID] = row.Title })
        ;(triggerColumns || []).forEach((column) => { if (column?.ID) result[column.ID] = column.Title })
        return result
    }, [triggerOptions, triggerRows, triggerColumns])

    return { triggerField, labels, loaded }
}

export default useShowWhenLabels
